import React, { useState, useEffect, useCallback } from 'react';
import { format, addDays, subDays, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';
import EXIF from 'exif-js';

const API_URL = process.env.REACT_APP_API_URL || '';

function App() {
  const [currentView, setCurrentView] = useState('entry');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [monthlyData, setMonthlyData] = useState({ entries: [], stats: {} });
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [photoModal, setPhotoModal] = useState(null);
  const [expandedMeals, setExpandedMeals] = useState({});
  const [sharedWith, setSharedWith] = useState([]);
  const [newShareEmail, setNewShareEmail] = useState('');
  const [shareLoading, setShareLoading] = useState(false);

  const toggleMealAccordion = (index) => {
    setExpandedMeals(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const formatDateForAPI = (date) => format(date, 'yyyy-MM-dd');
  const formatDateDisplay = (date) => format(date, 'dd/MM/yyyy');
  const formatDateLong = (date) => format(date, 'EEEE, MMMM do, yyyy');

  // Common supplements list
  const commonSupplements = ['B12', 'Vitamin D', 'Vitamin K2', 'B Complex', 'Omega-3', 'Magnesium', 'Zinc', 'Iron'];
  const [customSupplement, setCustomSupplement] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    wake_up_time: '',
    bed_time: '',
    activity: '',
    water_intake: '',
    sleep_hours: '',
    quick_notes: '',
    supplements: [],
    meals: []
  });

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(''), 3000);
  };

  // Calculate sleep hours from bed time and wake up time
  const calculateSleepHours = (bedTime, wakeUpTime) => {
    if (!bedTime || !wakeUpTime) return '';

    const [bedHours, bedMins] = bedTime.split(':').map(Number);
    const [wakeHours, wakeMins] = wakeUpTime.split(':').map(Number);

    let bedMinutes = bedHours * 60 + bedMins;
    let wakeMinutes = wakeHours * 60 + wakeMins;

    // If wake time is earlier than bed time, assume it's the next day
    if (wakeMinutes <= bedMinutes) {
      wakeMinutes += 24 * 60;
    }

    const diffMinutes = wakeMinutes - bedMinutes;
    const hours = Math.floor(diffMinutes / 60);
    const mins = diffMinutes % 60;

    if (mins === 0) {
      return `${hours} hrs`;
    }
    return `${hours} hrs ${mins} min`;
  };

  // Update form field and auto-calculate sleep if applicable
  const updateFormField = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      // Auto-calculate sleep hours when both times are set
      if ((field === 'wake_up_time' || field === 'bed_time') && updated.wake_up_time && updated.bed_time) {
        updated.sleep_hours = calculateSleepHours(updated.bed_time, updated.wake_up_time);
      }

      return updated;
    });
  };

  const fetchEntry = useCallback(async () => {
    setLoading(true);
    setExpandedMeals({}); // Reset accordion state when loading new entry
    try {
      const response = await fetch(`${API_URL}/api/entries/${formatDateForAPI(selectedDate)}`, {
        credentials: 'include'
      });
      const data = await response.json();
      console.log('Fetched entry:', data);
      console.log('Meals:', data?.meals);

      if (data) {
        setEntry(data);
        // Auto-expand only the latest meal (by time, last in sorted order)
        if (data.meals && data.meals.length > 0) {
          const sorted = [...data.meals].sort((a, b) => {
            if (!a.time) return 1;
            if (!b.time) return -1;
            return a.time.localeCompare(b.time);
          });
          const lastMeal = sorted[sorted.length - 1];
          setExpandedMeals({ [lastMeal.id]: true });
        }
        setFormData({
          wake_up_time: data.wake_up_time || '',
          bed_time: data.bed_time || '',
          activity: data.activity || '',
          water_intake: data.water_intake || '',
          sleep_hours: data.sleep_hours || '',
          quick_notes: data.quick_notes || '',
          supplements: data.supplements || [],
          meals: data.meals || []
        });
      } else {
        setEntry(null);
        setFormData({
          wake_up_time: '',
          bed_time: '',
          activity: '',
          water_intake: '',
          sleep_hours: '',
          quick_notes: '',
          meals: [{ time: '', description: '', photo: '', meal_type: '' }],
          supplements: []
        });
      }
    } catch (error) {
      console.error('Error fetching entry:', error);
      showToast('Error loading entry');
    }
    setLoading(false);
  }, [selectedDate]);

  const fetchGallery = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/photos`, {
        credentials: 'include'
      });
      const data = await response.json();
      setGalleryPhotos(data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    }
  }, []);

  const fetchMonthly = useCallback(async () => {
    try {
      const month = String(selectedMonth.getMonth() + 1);
      const year = String(selectedMonth.getFullYear());
      const response = await fetch(`${API_URL}/api/summary/month?month=${month}&year=${year}`, {
        credentials: 'include'
      });
      const data = await response.json();
      setMonthlyData(data);
    } catch (error) {
      console.error('Error fetching monthly:', error);
    }
  }, [selectedMonth]);

  const fetchSharedWith = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/share`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSharedWith(data);
      }
    } catch (error) {
      console.error('Error fetching shared access:', error);
    }
  }, []);

  const addShareAccess = async () => {
    if (!newShareEmail.trim() || !newShareEmail.includes('@')) {
      showToast('Please enter a valid email');
      return;
    }

    setShareLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: newShareEmail.trim() })
      });

      if (response.ok) {
        setNewShareEmail('');
        fetchSharedWith();
        showToast('Access granted');
      } else {
        const data = await response.json();
        showToast(data.error || 'Failed to add access');
      }
    } catch (error) {
      console.error('Error adding share access:', error);
      showToast('Network error');
    }
    setShareLoading(false);
  };

  const removeShareAccess = async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/share/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchSharedWith();
        showToast('Access removed');
      } else {
        showToast('Failed to remove access');
      }
    } catch (error) {
      console.error('Error removing share access:', error);
      showToast('Network error');
    }
  };

  useEffect(() => {
    if (currentView === 'entry') {
      // Always show fresh form in Write tab
      setFormData({
        wake_up_time: '',
        bed_time: '',
        activity: '',
        water_intake: '',
        sleep_hours: '',
        quick_notes: '',
        meals: [{ time: '', description: '', photo: '', meal_type: '' }]
      });
    } else if (currentView === 'summary') {
      fetchEntry();
    } else if (currentView === 'gallery') {
      fetchGallery();
    } else if (currentView === 'monthly') {
      fetchMonthly();
    } else if (currentView === 'settings') {
      fetchSharedWith();
    }
  }, [currentView, selectedDate, fetchEntry, fetchGallery, fetchMonthly, fetchSharedWith]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: formatDateForAPI(selectedDate),
          ...formData
        })
      });

      if (response.ok) {
        const data = await response.json();
        setEntry(data);
        showToast('Journal entry saved!');
        // Clear form after saving
        setFormData({
          wake_up_time: '',
          bed_time: '',
          activity: '',
          water_intake: '',
          sleep_hours: '',
          quick_notes: '',
          meals: [{ time: '', description: '', photo: '', meal_type: '' }],
          supplements: []
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error response:', response.status, errorData);
        showToast(errorData.error || 'Error saving entry');
      }
    } catch (error) {
      console.error('Error saving:', error);
      showToast('Network error - please try again');
    }
    setSaving(false);
  };

  const [mealToDelete, setMealToDelete] = useState(null);

  const handleDeleteMeal = async (mealId) => {
    try {
      const response = await fetch(`${API_URL}/api/meals/${mealId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        // Remove meal from entry state
        setEntry(prev => ({
          ...prev,
          meals: prev.meals.filter(m => m.id !== mealId)
        }));
        setMealToDelete(null);
        showToast('Meal deleted');
      } else {
        const errorData = await response.json().catch(() => ({}));
        showToast(errorData.error || 'Error deleting meal');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      showToast('Network error - please try again');
    }
  };

  const addMeal = () => {
    setFormData({
      ...formData,
      meals: [...formData.meals, { time: '', description: '', photo: '', meal_type: '' }]
    });
  };

  const updateMeal = (index, field, value) => {
    const newMeals = [...formData.meals];
    newMeals[index] = { ...newMeals[index], [field]: value };
    setFormData({ ...formData, meals: newMeals });
  };

  const deleteMeal = (index) => {
    const newMeals = formData.meals.filter((_, i) => i !== index);
    setFormData({ ...formData, meals: newMeals });
  };

  const toggleSupplement = (supplement) => {
    const current = formData.supplements || [];
    if (current.includes(supplement)) {
      setFormData({ ...formData, supplements: current.filter(s => s !== supplement) });
    } else {
      setFormData({ ...formData, supplements: [...current, supplement] });
    }
  };

  const addCustomSupplement = () => {
    if (customSupplement.trim() && !formData.supplements?.includes(customSupplement.trim())) {
      setFormData({
        ...formData,
        supplements: [...(formData.supplements || []), customSupplement.trim()]
      });
      setCustomSupplement('');
    }
  };

  // Extract EXIF date/time from image file using exif-js
  const extractExifTime = (file) => {
    return new Promise((resolve) => {
      EXIF.getData(file, function() {
        const dateTimeOriginal = EXIF.getTag(this, 'DateTimeOriginal');
        const dateTimeDigitized = EXIF.getTag(this, 'DateTimeDigitized');
        const dateTime = dateTimeOriginal || dateTimeDigitized;

        if (dateTime) {
          // Format: "YYYY:MM:DD HH:MM:SS" -> extract time as "HH:MM"
          const timePart = dateTime.split(' ')[1];
          if (timePart) {
            const [hours, minutes] = timePart.split(':');
            resolve(`${hours}:${minutes}`);
            return;
          }
        }
        resolve(null);
      });
    });
  };

  const compressImage = (file, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Scale down if wider than maxWidth
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to compressed JPEG
          const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedDataUrl);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoUpload = async (index, event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        // Extract EXIF time and compress image in parallel
        const [exifTime, compressedPhoto] = await Promise.all([
          extractExifTime(file),
          compressImage(file)
        ]);

        // Update both photo and time in a single state update to avoid race condition
        setFormData(prev => {
          const newMeals = [...prev.meals];
          newMeals[index] = {
            ...newMeals[index],
            photo: compressedPhoto,
            // Only set time if it's empty and we have EXIF time
            time: (!newMeals[index].time && exifTime) ? exifTime : newMeals[index].time
          };
          return { ...prev, meals: newMeals };
        });

        if (exifTime && !formData.meals[index]?.time) {
          showToast('Time filled from photo');
        }
      } catch (error) {
        console.error('Error processing image:', error);
        showToast('Error uploading photo');
      }
    }
  };

  const navigateDate = (direction) => {
    setSelectedDate(direction === 'prev' ? subDays(selectedDate, 1) : addDays(selectedDate, 1));
  };

  const navigateMonth = (direction) => {
    setSelectedMonth(direction === 'prev' ? subMonths(selectedMonth, 1) : addMonths(selectedMonth, 1));
  };

  const renderNav = () => (
    <nav className="nav">
      <button
        className={`nav-btn ${currentView === 'entry' ? 'active' : ''}`}
        onClick={() => setCurrentView('entry')}
      >
        Write
      </button>
      <button
        className={`nav-btn ${currentView === 'summary' ? 'active' : ''}`}
        onClick={() => setCurrentView('summary')}
      >
        Today
      </button>
      <button
        className={`nav-btn ${currentView === 'gallery' ? 'active' : ''}`}
        onClick={() => setCurrentView('gallery')}
      >
        Photos
      </button>
      <button
        className={`nav-btn ${currentView === 'monthly' ? 'active' : ''}`}
        onClick={() => setCurrentView('monthly')}
      >
        Calendar
      </button>
      <button
        className={`nav-btn settings-btn ${currentView === 'settings' ? 'active' : ''}`}
        onClick={() => setCurrentView('settings')}
        title="Settings"
      >
        &#9881;
      </button>
    </nav>
  );

  const renderDatePicker = () => (
    <div className="date-picker-container">
      <button className="date-nav-btn" onClick={() => navigateDate('prev')}>
        &#8249;
      </button>
      <div className="date-display" onClick={() => setSelectedDate(new Date())}>
        {formatDateDisplay(selectedDate)}
      </div>
      <button className="date-nav-btn" onClick={() => navigateDate('next')}>
        &#8250;
      </button>
    </div>
  );

  const renderEntryForm = () => (
    <div className="journal-page">
      <div className="page-header">
        <h1>My Food Journal</h1>
        <div className="date">{formatDateLong(selectedDate)}</div>
      </div>

      {renderDatePicker()}

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <>
          {/* Wake Up & Bed Time */}
          <div className="entry-section">
            <div className="entry-section-title">
              <span className="icon">&#9728;</span> Day Schedule
            </div>
            <div className="quick-info-row">
              <div className="quick-info-item">
                <div className="quick-info-label">Wake Up</div>
                <input
                  type="time"
                  className="quick-info-input"
                  value={formData.wake_up_time}
                  onChange={(e) => updateFormField('wake_up_time', e.target.value)}
                />
              </div>
              <div className="quick-info-item">
                <div className="quick-info-label">Bed Time</div>
                <input
                  type="time"
                  className="quick-info-input"
                  value={formData.bed_time}
                  onChange={(e) => updateFormField('bed_time', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Meals */}
          <div className="entry-section">
            <div className="entry-section-title">
              <span className="icon">&#127860;</span> Meals & Snacks
            </div>

            {formData.meals.map((meal, index) => (
              <div key={index} className="meal-card">
                <div className="meal-card-header">
                  <input
                    type="time"
                    className="time-input"
                    value={meal.time}
                    onChange={(e) => updateMeal(index, 'time', e.target.value)}
                    placeholder="Time"
                  />
                  <button
                    className="delete-meal-btn"
                    onClick={() => deleteMeal(index)}
                    title="Remove meal"
                  >
                    &#10005;
                  </button>
                </div>

                <textarea
                  className="description-input"
                  value={meal.description}
                  onChange={(e) => updateMeal(index, 'description', e.target.value)}
                  placeholder="What did you eat? e.g., Chicken egg omelette with vegetables"
                />

                <div className={`photo-upload-area ${meal.photo ? 'has-photo' : ''}`}>
                  {meal.photo ? (
                    <>
                      <img src={meal.photo} alt="Meal" className="meal-photo" />
                      <div className="photo-actions">
                        <label className="photo-action-btn">
                          Change
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handlePhotoUpload(index, e)}
                            style={{ display: 'none' }}
                          />
                        </label>
                        <button
                          className="photo-action-btn"
                          onClick={() => updateMeal(index, 'photo', '')}
                        >
                          Remove
                        </button>
                      </div>
                    </>
                  ) : (
                    <label style={{ cursor: 'pointer', display: 'block' }}>
                      <div className="photo-upload-icon">&#128247;</div>
                      <div className="photo-upload-text">Tap to add photo</div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePhotoUpload(index, e)}
                        style={{ display: 'none' }}
                      />
                    </label>
                  )}
                </div>
              </div>
            ))}

            <button className="add-meal-btn" onClick={addMeal}>
              <span>+</span> Add another meal
            </button>
          </div>

          {/* Activity & Health */}
          <div className="entry-section">
            <div className="entry-section-title">
              <span className="icon">&#127939;</span> Activity & Health
            </div>
            <div className="quick-info-row">
              <div className="quick-info-item">
                <div className="quick-info-label">Activity</div>
                <input
                  type="text"
                  className="quick-info-input"
                  value={formData.activity}
                  onChange={(e) => setFormData({ ...formData, activity: e.target.value })}
                  placeholder="e.g., Gym (8:30-9:30 am)"
                />
              </div>
              <div className="quick-info-item">
                <div className="quick-info-label">Water Intake</div>
                <input
                  type="text"
                  className="quick-info-input"
                  value={formData.water_intake}
                  onChange={(e) => setFormData({ ...formData, water_intake: e.target.value })}
                  placeholder="e.g., 2 litres"
                />
              </div>
            </div>
            <div className="quick-info-row">
              <div className="quick-info-item">
                <div className="quick-info-label">Sleep Duration</div>
                <input
                  type="text"
                  className="quick-info-input"
                  value={formData.sleep_hours}
                  onChange={(e) => setFormData({ ...formData, sleep_hours: e.target.value })}
                  placeholder="e.g., 6 hrs"
                />
              </div>
            </div>
          </div>

          {/* Supplements */}
          <div className="entry-section">
            <div className="entry-section-title">
              <span className="icon">&#128138;</span> Supplements
            </div>
            <div className="supplements-grid">
              {commonSupplements.map((supplement) => (
                <button
                  key={supplement}
                  type="button"
                  className={`supplement-chip ${formData.supplements?.includes(supplement) ? 'selected' : ''}`}
                  onClick={() => toggleSupplement(supplement)}
                >
                  {formData.supplements?.includes(supplement) && <span className="check">&#10003;</span>}
                  {supplement}
                </button>
              ))}
              {/* Show custom supplements that aren't in the common list */}
              {formData.supplements?.filter(s => !commonSupplements.includes(s)).map((supplement) => (
                <button
                  key={supplement}
                  type="button"
                  className="supplement-chip selected"
                  onClick={() => toggleSupplement(supplement)}
                >
                  <span className="check">&#10003;</span>
                  {supplement}
                </button>
              ))}
            </div>
            <div className="supplement-custom">
              <input
                type="text"
                placeholder="Add other supplement..."
                value={customSupplement}
                onChange={(e) => setCustomSupplement(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addCustomSupplement()}
              />
              <button type="button" onClick={addCustomSupplement}>Add</button>
            </div>
          </div>

          {/* Quick Notes */}
          <div className="entry-section">
            <div className="entry-section-title">
              <span className="icon">&#128221;</span> Quick Notes
            </div>
            <textarea
              className="notes-textarea"
              value={formData.quick_notes}
              onChange={(e) => setFormData({ ...formData, quick_notes: e.target.value })}
              placeholder="How did you feel today? Any observations about your meals, energy levels, digestion..."
            />
          </div>

          <button
            className={`save-btn ${saving ? 'saving' : ''}`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Entry'}
          </button>
        </>
      )}
    </div>
  );

  const formatTime12h = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getTimePeriod = (time24) => {
    if (!time24) return 'Other';
    const hour = parseInt(time24.split(':')[0], 10);
    if (hour >= 5 && hour < 12) return 'Morning';
    if (hour >= 12 && hour < 17) return 'Afternoon';
    if (hour >= 17 && hour < 21) return 'Evening';
    return 'Night';
  };

  const sortAndGroupMeals = (meals) => {
    if (!meals || meals.length === 0) return {};

    // Sort meals by time
    const sorted = [...meals].sort((a, b) => {
      if (!a.time) return 1;
      if (!b.time) return -1;
      return a.time.localeCompare(b.time);
    });

    // Group by time period
    const groups = { Morning: [], Afternoon: [], Evening: [], Night: [], Other: [] };
    sorted.forEach(meal => {
      const period = getTimePeriod(meal.time);
      groups[period].push(meal);
    });

    // Remove empty groups
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) delete groups[key];
    });

    return groups;
  };

  const renderDailySummary = () => (
    <div className="daily-summary">
      <div className="summary-header">
        <h2>Daily Summary</h2>
        <div className="summary-date">{formatDateLong(selectedDate)}</div>
      </div>

      {renderDatePicker()}

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
        </div>
      ) : !entry ? (
        <div className="empty-state">
          <div className="empty-icon">&#128214;</div>
          <div className="empty-text">No entry for this day yet</div>
        </div>
      ) : (
        <>
          <div className="summary-stats">
            <div className="stat-item">
              <div className="stat-value">{formatTime12h(entry.wake_up_time) || '-'}</div>
              <div className="stat-label">Wake Up</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{formatTime12h(entry.bed_time) || '-'}</div>
              <div className="stat-label">Bed Time</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{entry.water_intake || '-'}</div>
              <div className="stat-label">Water</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{entry.sleep_hours || '-'}</div>
              <div className="stat-label">Sleep</div>
            </div>
          </div>

          {entry.activity && (
            <div className="summary-notes" style={{ marginBottom: '16px' }}>
              <h3>Activity</h3>
              <p>{entry.activity}</p>
            </div>
          )}

          {entry.supplements && entry.supplements.length > 0 && (
            <div className="summary-notes" style={{ marginBottom: '16px' }}>
              <h3>Supplements</h3>
              <div className="supplements-grid" style={{ marginTop: '8px' }}>
                {entry.supplements.map((supplement, idx) => (
                  <span key={idx} className="supplement-chip selected" style={{ cursor: 'default' }}>
                    {supplement}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="meals-section">
            <div className="meals-section-title">
              <span className="icon">&#127860;</span> Meals ({entry.meals?.length || 0})
            </div>
            {entry.meals && entry.meals.length > 0 ? (
              Object.entries(sortAndGroupMeals(entry.meals)).map(([period, meals]) => (
                <div key={period} className="meal-period-group">
                  <div className="meal-period-header">{period}</div>
                  {meals.map((meal) => (
                    <div key={meal.id} className={`accordion-meal ${expandedMeals[meal.id] ? 'expanded' : ''}`}>
                      <div
                        className="accordion-header"
                        onClick={() => toggleMealAccordion(meal.id)}
                      >
                        <div className="accordion-title">
                          <span className="accordion-time">{formatTime12h(meal.time) || 'No time'}</span>
                          <span className="accordion-preview">
                            {meal.description ? (meal.description.length > 30 ? meal.description.substring(0, 30) + '...' : meal.description) : 'No description'}
                          </span>
                        </div>
                        <span className="accordion-icon">{expandedMeals[meal.id] ? '−' : '+'}</span>
                      </div>
                      {expandedMeals[meal.id] && (
                        <div className="accordion-content">
                          <div className="accordion-description">{meal.description}</div>
                          {meal.photo && (
                            <img
                              src={meal.photo}
                              alt="Meal"
                              className="summary-meal-photo"
                              onClick={() => setPhotoModal({ photo: meal.photo, description: meal.description, time: meal.time })}
                            />
                          )}
                          <button
                            className="delete-meal-item-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMealToDelete(meal);
                            }}
                          >
                            Delete Meal
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div className="no-meals-message">No meals logged yet</div>
            )}
          </div>

          {entry.quick_notes && (
            <div className="summary-notes">
              <h3>Notes</h3>
              <p>{entry.quick_notes}</p>
            </div>
          )}

          {/* Delete Meal Confirmation Modal */}
          {mealToDelete && (
            <div className="confirm-modal-overlay" onClick={() => setMealToDelete(null)}>
              <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                <h3>Delete Meal?</h3>
                <p>Are you sure you want to delete this meal{mealToDelete.time ? ` at ${formatTime12h(mealToDelete.time)}` : ''}? This cannot be undone.</p>
                <div className="confirm-modal-actions">
                  <button className="confirm-btn cancel" onClick={() => setMealToDelete(null)}>
                    Cancel
                  </button>
                  <button className="confirm-btn delete" onClick={() => handleDeleteMeal(mealToDelete.id)}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderGallery = () => (
    <div className="gallery-page">
      <div className="page-header">
        <h1>Photo Gallery</h1>
        <div className="date">All your meal memories</div>
      </div>

      {galleryPhotos.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">&#128247;</div>
          <div className="empty-text">No photos yet. Start adding meal photos!</div>
        </div>
      ) : (
        <div className="gallery-grid">
          {galleryPhotos.map((item, index) => (
            <div
              key={index}
              className="gallery-item"
              onClick={() => setPhotoModal(item)}
            >
              <img src={item.photo} alt={item.description} className="gallery-photo" />
              <div className="gallery-overlay">
                <div className="gallery-date">
                  {format(new Date(item.date), 'dd/MM')} - {formatTime12h(item.time)}
                </div>
                <div className="gallery-desc">{item.description}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderMonthlyCalendar = () => {
    const monthStart = startOfMonth(selectedMonth);
    const monthEnd = endOfMonth(selectedMonth);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const startDay = getDay(monthStart);

    const entryDates = new Set(monthlyData.entries?.map(e => e.date) || []);
    const emptyDays = Array(startDay).fill(null);

    return (
      <div className="monthly-summary">
        <div className="page-header">
          <h1>Monthly View</h1>
        </div>

        <div className="month-selector">
          <button className="date-nav-btn" onClick={() => navigateMonth('prev')}>
            &#8249;
          </button>
          <div className="month-display">
            {format(selectedMonth, 'MMMM yyyy')}
          </div>
          <button className="date-nav-btn" onClick={() => navigateMonth('next')}>
            &#8250;
          </button>
        </div>

        <div className="month-stats">
          <div className="month-stat">
            <div className="month-stat-value">{monthlyData.stats?.totalEntries || 0}</div>
            <div className="month-stat-label">Days Logged</div>
          </div>
          <div className="month-stat">
            <div className="month-stat-value">{monthlyData.stats?.totalMeals || 0}</div>
            <div className="month-stat-label">Total Meals</div>
          </div>
          <div className="month-stat">
            <div className="month-stat-value">{monthlyData.stats?.avgMealsPerDay || 0}</div>
            <div className="month-stat-label">Avg Meals/Day</div>
          </div>
        </div>

        <div className="calendar-view">
          <div className="calendar-header">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="calendar-day-name">{day}</div>
            ))}
          </div>
          <div className="calendar-grid">
            {emptyDays.map((_, i) => (
              <div key={`empty-${i}`} className="calendar-day empty"></div>
            ))}
            {days.map(day => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const hasEntry = entryDates.has(dateStr);
              const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

              return (
                <div
                  key={dateStr}
                  className={`calendar-day ${hasEntry ? 'has-entry' : ''} ${isToday ? 'today' : ''}`}
                  onClick={() => {
                    setSelectedDate(day);
                    setCurrentView('summary');
                  }}
                >
                  {format(day, 'd')}
                  {hasEntry && <div className="calendar-dot"></div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderPhotoModal = () => {
    if (!photoModal) return null;

    return (
      <div className="photo-modal" onClick={() => setPhotoModal(null)}>
        <button className="photo-modal-close" onClick={() => setPhotoModal(null)}>
          &#10005;
        </button>
        <img src={photoModal.photo} alt={photoModal.description} />
        <div className="photo-modal-info">
          {photoModal.date && <div>{format(new Date(photoModal.date), 'dd/MM/yyyy')}</div>}
          <div>{formatTime12h(photoModal.time)} - {photoModal.description}</div>
        </div>
      </div>
    );
  };

  const renderSettings = () => (
    <div className="settings-page">
      <div className="page-header">
        <h1>Settings</h1>
        <div className="date">Manage your journal</div>
      </div>

      <div className="settings-section">
        <h2 className="settings-section-title">
          <span className="icon">&#128101;</span> Share Your Journal
        </h2>
        <p className="settings-description">
          Allow specific people to view your journal entries. They will have read-only access to your Today, Calendar, and Photos views.
        </p>

        <div className="share-input-row">
          <input
            type="email"
            className="share-email-input"
            placeholder="Enter email address..."
            value={newShareEmail}
            onChange={(e) => setNewShareEmail(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addShareAccess()}
          />
          <button
            className="share-add-btn"
            onClick={addShareAccess}
            disabled={shareLoading}
          >
            {shareLoading ? '...' : 'Add'}
          </button>
        </div>

        <div className="shared-users-list">
          {sharedWith.length === 0 ? (
            <div className="no-shares">
              <span className="icon">&#128274;</span>
              <span>Your journal is private. Add emails above to share.</span>
            </div>
          ) : (
            <>
              <div className="shared-users-header">
                Shared with {sharedWith.length} {sharedWith.length === 1 ? 'person' : 'people'}
              </div>
              {sharedWith.map((share) => (
                <div key={share.id} className="shared-user-item">
                  <div className="shared-user-email">{share.shared_with_email}</div>
                  <button
                    className="shared-user-remove"
                    onClick={() => removeShareAccess(share.shared_with_email)}
                    title="Remove access"
                  >
                    &#10005;
                  </button>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="app">
      {renderNav()}

      {currentView === 'entry' && renderEntryForm()}
      {currentView === 'summary' && renderDailySummary()}
      {currentView === 'gallery' && renderGallery()}
      {currentView === 'monthly' && renderMonthlyCalendar()}
      {currentView === 'settings' && renderSettings()}

      {renderPhotoModal()}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

export default App;
